import { forwardRef } from 'react';
import type { PropsWithoutRef, Ref, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import Text from '../Text';
import Button from '../Button';
import ExpandCollapse from '../ExpandCollapse';
import Flex from '../Flex';
import Grid from '../Grid';
import HTML from '../HTML';
import Icon, { StyledIcon, registerIcon } from '../Icon';
import Banner from '../Banner';
import type { BannerProps } from '../Banner';
import * as caretRightIcon from '../Icon/icons/caret-right.icon';
import * as warnSolidIcon from '../Icon/icons/warn-solid.icon';
import Actions from '../Actions';
import type { ActionsProps } from '../Actions';
import { defaultThemeProp } from '../../theme';
import type { BaseProps, ForwardRefForwardPropsComponent, HeadingTag } from '../../types';
import { useDirection, useI18n, useUID } from '../../hooks';
import { calculateFontSize, omitProps } from '../../styles';
import type { FontSize } from '../../styles';
import AdditionalInfo from '../AdditionalInfo';
import type { AdditionalInfoProps } from '../AdditionalInfo';

registerIcon(caretRightIcon);
registerIcon(warnSolidIcon);

// Types
export type FieldGroupProps = BaseProps & {
  /** The content of the Field Group. */
  children: ReactNode;
  /** A description of the field group to be displayed above the controls. */
  description?: string;
  /** A banner to be displayed above the controls. */
  banner?: BannerProps;
  /** The display and markup variant to apply to the group. */
  variant?: 'form-group';
  /** Indicates validation status for error display in the header. */
  status?: 'error';
  /** A reference to the wrapping element. */
  ref?: Ref<HTMLFieldSetElement>;
} & (
    | {
        /** The name associated with the Field Group. */
        name: string;
        /** Provides a contextual label for the name. */
        contextualLabel?: string;
        /** Select the heading tag for header */
        headingTag?: HeadingTag;
        /** Provides additional info relevant to the field group. */
        additionalInfo?: {
          heading?: AdditionalInfoProps['heading'];
          content: AdditionalInfoProps['children'];
        };
        /** An array of actions to add to the header. */
        actions?: ActionsProps['items'];
        /**
         * Controls the collapsed state of the field group.
         * Passing undefined will render the field group as non-collapsible.
         * @default false
         */
        collapsed?: boolean;
        /**
         * Called when user clicks the toggle button.
         */
        onToggleCollapsed?: () => void;
      }
    | {
        name?: undefined;
        contextualLabel?: never;
        headingTag?: never;
        additionalInfo?: never;
        actions?: never;
        collapsed?: never;
        onToggleCollapsed?: never;
      }
  );

interface FieldGroupLegendProps extends Pick<FieldGroupProps, 'collapsed'> {
  name: NonNullable<FieldGroupProps['name']>;
  actions?: ActionsProps['items'];
  children?: ReactNode;
  additionalInfo?: FieldGroupProps['additionalInfo'];
  contextualLabel?: FieldGroupProps['contextualLabel'];
}

// Styles
const StyledExpandButton = styled(Button)(({
  theme: {
    base: { animation }
  }
}) => {
  const { rtl } = useDirection();
  return css`
    ${StyledIcon} {
      transition: transform ${animation.speed} ${animation.timing.ease};
    }
    &[aria-expanded='true'] ${StyledIcon}:first-child {
      transform: rotate(90deg);
    }
    &[aria-expanded='false'] ${StyledIcon}:first-child {
      transform: rotate(${rtl ? 180 : 0}deg);
    }
  `;
});

StyledExpandButton.defaultProps = defaultThemeProp;

const StyledErrorIcon = styled(Icon)(
  ({
    theme: {
      base: { palette }
    }
  }) => css`
    color: ${palette.urgent};
  `
);

StyledErrorIcon.defaultProps = defaultThemeProp;

export const StyledFieldGroupName = styled.span``;

export const StyledFieldHeader = styled(Flex)`
  word-break: break-word;
`;

export const StyledFieldGroupContent = styled(Grid)``;

export const StyledFieldGroupLegend = styled.legend.withConfig(omitProps('collapsed'))<
  Pick<FieldGroupProps, 'collapsed'>
>(
  ({
    collapsed,
    theme: {
      base: {
        spacing,
        animation,
        'hit-area': { 'mouse-min': hitAreaMouse, 'finger-min': hitAreaFinger }
      }
    }
  }) => {
    return css`
      width: 100%;
      margin-bottom: 0;
      ${!collapsed &&
      css`
        margin-block-end: calc(1.5 * ${spacing});
      `}
      transition: margin-block-end ${animation.speed} ${animation.timing.ease};

      > ${StyledExpandButton} {
        min-height: ${hitAreaMouse};

        @media (pointer: 'coarse') {
          min-height: ${hitAreaFinger};
        }
      }
    `;
  }
);

StyledFieldGroupLegend.defaultProps = defaultThemeProp;

export const StyledFieldGroup = styled.fieldset(
  ({
    theme: {
      base: { 'font-size': fontSize, 'font-scale': fontScale },
      components: { text }
    }
  }) => {
    const fontSizes = calculateFontSize(fontSize, fontScale);

    return css`
      border: none;

      & ${StyledFieldGroupName} {
        font-size: ${fontSizes[text.h3['font-size'] as FontSize]};
        font-weight: ${text.h3['font-weight']};
      }

      & & ${StyledFieldGroupName} {
        font-size: ${fontSizes[text.h4['font-size'] as FontSize]};
        font-weight: ${text.h4['font-weight']};
      }

      & & & ${StyledFieldGroupName} {
        font-size: ${fontSizes[text.h5['font-size'] as FontSize]};
        font-weight: ${text.h5['font-weight']};
      }

      & & & & ${StyledFieldGroupName} {
        font-size: ${fontSizes[text.h6['font-size'] as FontSize]};
        font-weight: ${text.h6['font-weight']};
      }
    `;
  }
);

StyledFieldGroup.defaultProps = defaultThemeProp;

export const StyledGroupContainer = styled.div<{ border?: boolean }>(({ theme, border }) => {
  const {
    base: {
      palette: { 'border-line': borderColor },
      spacing,
      'border-radius': baseBorderRadius
    },
    components: {
      'form-control': { 'border-width': borderWidth }
    }
  } = theme;
  if (!border) return null;

  return css`
    padding: ${spacing};
    border-radius: calc(${baseBorderRadius} / 2);
    border: ${borderWidth} solid ${borderColor};
  `;
});

StyledGroupContainer.defaultProps = defaultThemeProp;

// Components
const FieldGroupLegend = ({
  name,
  contextualLabel,
  children,
  collapsed,
  actions,
  additionalInfo
}: FieldGroupLegendProps) => {
  return (
    <StyledFieldGroupLegend collapsed={collapsed}>
      <Flex container={{ alignItems: 'center', justify: 'between', gap: 0.5 }}>
        <Flex container={{ alignItems: 'center', gap: 0.5 }} as={StyledFieldHeader}>
          {children}
          {additionalInfo && (
            <AdditionalInfo
              heading={additionalInfo.heading}
              contextualLabel={contextualLabel || name}
            >
              {additionalInfo.content}
            </AdditionalInfo>
          )}
        </Flex>
        {actions && <Actions items={actions} contextualLabel={contextualLabel} />}
      </Flex>
    </StyledFieldGroupLegend>
  );
};

export const StyledDescription = styled(HTML)`
  /* Prevent instruction text from contributing to modal width calculation */
  width: 0;
  min-width: 100%;
`;

const FieldGroup: ForwardRefForwardPropsComponent<FieldGroupProps> = forwardRef(function FieldGroup(
  {
    children,
    description,
    banner,
    name,
    contextualLabel,
    additionalInfo,
    actions,
    collapsed,
    headingTag,
    onToggleCollapsed,
    variant,
    status,
    ...restProps
  }: PropsWithoutRef<FieldGroupProps>,
  ref: FieldGroupProps['ref']
) {
  const t = useI18n();
  const uid = useUID();

  const descAndChildren = (
    <StyledFieldGroupContent container={{ cols: 'minmax(0, 1fr)', gap: 2 }}>
      {banner && <Banner {...banner} />}
      {description && (
        <StyledDescription id={`${uid}-description`} forwardedAs='p' content={description} />
      )}
      <div>{children}</div>
    </StyledFieldGroupContent>
  );

  return (
    <StyledGroupContainer border={variant === 'form-group'}>
      <StyledFieldGroup
        aria-label={contextualLabel || name}
        aria-describedby={description ? `${uid}-description` : undefined}
        as={name ? 'fieldset' : 'div'}
        {...restProps}
        ref={ref}
      >
        {name && (
          <FieldGroupLegend
            collapsed={collapsed}
            actions={actions}
            additionalInfo={additionalInfo}
            name={name}
            contextualLabel={contextualLabel}
          >
            {typeof collapsed === 'boolean' ? (
              <StyledExpandButton
                type='button'
                variant='text'
                onClick={() => {
                  onToggleCollapsed?.();
                }}
                aria-expanded={!collapsed}
                aria-label={t(collapsed ? 'expand_noun' : 'collapse_noun', [
                  contextualLabel || name
                ])}
              >
                <Flex
                  container={{ direction: 'row', alignItems: 'center', gap: 0.5 }}
                  forwardedAs='span'
                >
                  <Icon name='caret-right' />
                  {status === 'error' ? <StyledErrorIcon name='warn-solid' /> : null}
                  {headingTag ? (
                    <Text variant={headingTag}>{name}</Text>
                  ) : (
                    <StyledFieldGroupName>{name}</StyledFieldGroupName>
                  )}
                </Flex>
              </StyledExpandButton>
            ) : (
              <>
                {status === 'error' ? <StyledErrorIcon name='warn-solid' /> : null}
                {headingTag ? (
                  <Text variant={headingTag}>{name}</Text>
                ) : (
                  <StyledFieldGroupName>{name}</StyledFieldGroupName>
                )}
              </>
            )}
          </FieldGroupLegend>
        )}

        {typeof collapsed === 'boolean' ? (
          <ExpandCollapse collapsed={collapsed}>{descAndChildren}</ExpandCollapse>
        ) : (
          descAndChildren
        )}
      </StyledFieldGroup>
    </StyledGroupContainer>
  );
});

export default FieldGroup;
